import React, { memo, useState } from 'react';
import { runOnJS, useAnimatedReaction, useSharedValue } from 'react-native-reanimated';

import { EventInfo } from '@/api/events/types/types';
import { CloseIcon } from '@/assets/icons/close';
import { Freeze } from '@/components/Freeze';
import { SizedBox } from '@/components/SizedBox';
import { Box } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { Handle } from '@/feature/alerts/components/Modal';
import { useFullScreenPagerTransition } from '@/feature/circular-pager/FullScreenPagerTransitionProvider';
import { PlayerFiltersProvider } from '@/feature/lobby/hooks/use-player-filters';
import { common, withOpacity } from '@/styles/styles';
import { isUFC } from '@/utils/league';

import { EventDetailsScreen } from '../EventDetailsScreen';

/**
 * Wrapper component that lets us freeze any content based on the shouldFreeze prop
 * We also freeze the content until the fade/scale in animation is done (isSheetMountCompleted === true)
 */
export const EventDetailsSheetScreen = memo(
    ({
        shouldFreeze,
        eventInfo,
        isActive,
        eventDedicatedHeaderInfo,
    }: {
        isActive: boolean;
        shouldFreeze: boolean;
        index: number;
        eventInfo: EventInfo;
        eventDedicatedHeaderInfo: EventInfo | null;
        offset: number;
    }) => {
        const [shouldRenderEventScreen, setShouldRenderEventScreen] = useState(false);
        const { sheetMountAnimationProgress } = useFullScreenPagerTransition();
        const hasTriggeredRender = useSharedValue(false);

        useAnimatedReaction(
            () => sheetMountAnimationProgress.value,
            current => {
                if (current === null) return;
                if (!hasTriggeredRender.value) {
                    // To optimize performance, keep the inactive event sheets hidden at the start of the screen transition/mount.
                    // Reveal the inactive sheets only after 70 % of the mount animation has completed.
                    if (current > -0.7) {
                        hasTriggeredRender.value = true;
                        runOnJS(setShouldRenderEventScreen)(true);
                    }
                }
            }
        );

        // we want to freeze the following cases:
        // 1. if the sheet is outside of the viewport (shouldFreeze is true)
        // 2. if the sheet is still mounting (shouldRenderEventScreen is false) and is not active
        const freeze = shouldFreeze || (!shouldRenderEventScreen && !isActive);

        return (
            <Freeze freeze={freeze} placeholder={<EmptyModal />}>
                <PlayerFiltersProvider key={createEventDetailsScreenKey(eventInfo)}>
                    <EventDetailsScreen
                        isActive={isActive}
                        eventInfo={eventInfo}
                        eventDedicatedHeaderInfo={eventDedicatedHeaderInfo}
                    />
                </PlayerFiltersProvider>
            </Freeze>
        );
    }
);

EventDetailsSheetScreen.displayName = 'EventDetailsSheetScreen';

export const EmptyModal = memo(() => {
    return (
        <Box
            backgroundColor={ds.colorToken.background.neutral.base}
            width="100%"
            height="100%"
            paddingHorizontal={'s16'}
        >
            <Handle
                handleColor={withOpacity(ds.colorValue.background.neutral.inverse, 0.15)}
                handleContainerStyle={common.backgroundColorTransparent}
            />
            <SizedBox value={12} />
            <CloseIcon />
        </Box>
    );
});

export const createEventDetailsScreenKey = (eventInfo: EventInfo): string => {
    if (isUFC(eventInfo.league)) {
        return 'ufc-event-screen';
    } else {
        return `event-details-screen-${eventInfo.id}`;
    }
};
